import CrossIcon from "@/assets/FF5A4F_cross_icon.svg?react";
import PlusIcon from "@/assets/FFFFFF_plus_icon.svg?react";
import EditIcon from "@/assets/colorable/pen_edit.svg?react";
import clsxm from "@/lib/clsxm";
import {memo, useEffect, useMemo, useState, type FC} from "react";
import {useDropzone} from "react-dropzone";

const UserAvatar: FC<{
  defaultAvatar?: string | null;
  handleUpload: (file: File | null) => void;
}> = ({defaultAvatar, handleUpload}) => {
  const [file, setFile] = useState<File | null>(null);
  const onDrop = (acceptedFiles: File[]) => {
    if (!acceptedFiles.length) return;
    setFile(acceptedFiles[0]);
  };
  const shownImage = useMemo(() => {
    return file ? URL.createObjectURL(file!) : (defaultAvatar ?? "");
  }, [file, defaultAvatar]);
  const {getInputProps, getRootProps, isDragActive, isDragReject} = useDropzone({
    onDrop,
    accept: {
      "image/*": [],
    },
  });

  useEffect(() => {
    if (!file) return;
    handleUpload(file);
  }, [file, handleUpload]);

  return (
    <div {...getRootProps()}>
      <input {...getInputProps()} />
      {!shownImage ? (
        <div
          className={clsxm(
            "group border-primary-dark-solid hover:border-primary-midnight h-44 w-44 rounded-full border-2 border-dashed bg-transparent object-cover transition-all duration-150",
            isDragReject && "border-danger-medium-red",
            isDragActive && !isDragReject && "border-primary-midnight border-2",
          )}>
          <div className="flex h-full w-full items-center justify-center">
            {isDragReject ? (
              <CrossIcon
                className={clsxm(
                  "border-primary-light-solid bg-primary-dark-solid group-hover:bg-primary-midnight flex h-12 w-12 items-center justify-center rounded-full border p-3.5 shadow-md transition-all duration-150",
                  isDragActive && !isDragReject && "bg-primary-midnight",
                  isDragReject && "border-danger-medium-red bg-danger-light-red",
                )}
              />
            ) : (
              <PlusIcon
                className={clsxm(
                  "border-primary-light-solid bg-primary-dark-solid group-hover:bg-primary-midnight flex h-12 w-12 items-center justify-center rounded-full border p-3.5 shadow-md transition-all duration-150",
                  isDragActive && !isDragReject && "bg-primary-midnight",
                  isDragReject && "border-danger-medium-red bg-danger-light-red",
                )}
              />
            )}
          </div>
        </div>
      ) : (
        <div
          className={clsxm(
            "group border-primary-light-solid hover:border-primary-midnight relative h-44 w-44 rounded-full border-2 shadow-md transition-all duration-150",
            isDragReject && "border-danger-medium-red",
            isDragActive && !isDragReject && "border-primary-midnight border-2",
          )}>
          <img src={shownImage} alt="" className="h-full w-full rounded-full object-cover" />
          <div
            className={clsxm(
              "border-primary-light-solid group-hover:border-primary-midnight absolute top-0 right-0 flex h-12 w-12 items-center justify-center rounded-full border bg-white p-3 shadow-lg transition-all duration-150",
              isDragActive && !isDragReject && "border-primary-midnight bg-primary-midnight",
              isDragReject && "border-danger-medium-red bg-danger-light-red",
            )}>
            {isDragReject ? (
              <CrossIcon className="size-4 shrink-0" />
            ) : (
              <EditIcon
                className={
                  "h-[18px] w-[18px] transition-all duration-150 " +
                  (isDragActive && !isDragReject ? "text-white" : "text-primary-dark-solid")
                }
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(UserAvatar);
